import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Car,
  Receipt,
  XCircle,
  ShieldCheck,
  Briefcase,
  Award,
  AlertTriangle,
  Flag,
  Check,
  FileCheck,
  RotateCcw,
  AlertCircle,
  Info,
} from 'lucide-react';
import type { QueueClaim } from '../../../hooks/useApproval';
import {
  useApproveClaim,
  useRejectClaim,
  useFinalApproveClaim,
  useFinanceRejectClaim,
  useHrApproveClaim,
  useHrRejectClaim,
  useVerifyEntry,
  useMarkHardcopyReceived,
} from '../../../hooks/useApproval';
import { useExpenses } from '../../../hooks/useClaims';
import { describeFlagger, type ExpenseItem } from '../../../hooks/useClaims';
import { MileageFormModal } from '../../claims/MileageFormModal';
import { PettyCashFormModal } from '../../claims/PettyCashFormModal';
import { RejectionDialog } from './RejectionDialog';
import { ApiError } from '../../../lib/api';
import { cn } from '@rhpc/ui';
import { useAuthStore } from '../../../stores/authStore';
import { canFinalizeReview } from '../../../lib/reviewPermissions';
import { plural } from '@/lib/plural';

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function formatCurrency(amount: number) {
  return new Intl.NumberFormat('en-MY', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(amount);
}

function formatDate(isoDate: string) {
  return new Date(isoDate).toLocaleDateString('en-MY', {
    day: 'numeric', month: 'short', year: 'numeric',
  });
}

function formatMonth(isoDate: string) {
  return new Date(isoDate).toLocaleDateString('en-MY', {
    year: 'numeric', month: 'long',
  });
}

// ---------------------------------------------------------------------------
// Props
// ---------------------------------------------------------------------------

interface ClaimReviewPanelProps {
  claim: QueueClaim;
  mode: 'admin' | 'finance' | 'hr';
  onClose: () => void;
  onActionComplete: () => void;
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export function ClaimReviewPanel({ claim, mode, onClose, onActionComplete }: ClaimReviewPanelProps) {
  const navigate = useNavigate();
  const [showApproveConfirm, setShowApproveConfirm] = useState(false);
  const [showRejectDialog, setShowRejectDialog] = useState(false);
  const [actionError, setActionError] = useState<string | null>(null);
  const [selectedExpense, setSelectedExpense] = useState<ExpenseItem | null>(null);
  const [hardcopyReceived, setHardcopyReceived] = useState(!!claim.hardcopyReceived);

  useEffect(() => {
    setHardcopyReceived(!!claim.hardcopyReceived);
  }, [claim.hardcopyReceived]);

  // Flagging dialog state
  const [flagTarget, setFlagTarget] = useState<{
    entryId: string;
    entryType: 'mileage' | 'petty-cash';
    title: string;
  } | null>(null);
  const [flagReasonInput, setFlagReasonInput] = useState('');
  const [flagError, setFlagError] = useState<string | null>(null);

  const approveClaim    = useApproveClaim();
  const rejectClaim     = useRejectClaim();
  const finalApprove    = useFinalApproveClaim();
  const financeReject   = useFinanceRejectClaim();
  const hrApprove       = useHrApproveClaim();
  const hrReject        = useHrRejectClaim();
  const verifyEntry     = useVerifyEntry(claim.id);
  const markHardcopy    = useMarkHardcopyReceived(claim.id);

  const { data: expensesData, isLoading: expensesLoading } = useExpenses(claim.id);

  const isAdminMode    = mode === 'admin';
  const isFinanceMode  = mode === 'finance';
  const isHrMode       = mode === 'hr';
  const viewerRole     = useAuthStore((state) => state.user?.role);
  const canFinalize    = canFinalizeReview(mode, viewerRole);
  // API only returns the claim to Admin when flagged from these statuses
  const flagReturnsClaim = claim.status === 'ADMIN_APPROVED' || claim.status === 'FINANCE_APPROVED';
  const isLoading      = approveClaim.isPending || rejectClaim.isPending || finalApprove.isPending || financeReject.isPending || hrApprove.isPending || hrReject.isPending;

  const expenses = expensesData?.expenses ?? [];
  const totalExpenses = expenses.length;

  const isEntryVerifiedForRole = (entry: ExpenseItem) => {
    if (isAdminMode) return !!entry.adminVerified && !entry.flagReason;
    if (isFinanceMode) return !!entry.financeVerified && !entry.flagReason;
    if (isHrMode) return !!entry.hrVerified && !entry.flagReason;
    return false;
  };

  const verifiedCount = expenses.filter(isEntryVerifiedForRole).length;
  const allVerified = totalExpenses > 0 && verifiedCount === totalExpenses;
  const anyFlagged = expenses.some((e) => !!e.flagReason);
  const hardcopySatisfied = isAdminMode || !!hardcopyReceived;
  const canApprove = allVerified && !anyFlagged && hardcopySatisfied;

  const approveLabel = isAdminMode
    ? 'Approve for Finance'
    : isFinanceMode
    ? 'Approve for HR'
    : 'Approve for Payment';

  const handleApprove = async () => {
    setActionError(null);
    try {
      if (isAdminMode) {
        await approveClaim.mutateAsync(claim.id);
      } else if (isFinanceMode) {
        await finalApprove.mutateAsync(claim.id);
      } else if (isHrMode) {
        await hrApprove.mutateAsync(claim.id);
      }
      setShowApproveConfirm(false);
      onActionComplete();
    } catch (err) {
      if (err instanceof ApiError) setActionError(err.message);
    }
  };

  const handleReject = async (reason: string) => {
    setActionError(null);
    try {
      if (isAdminMode) {
        await rejectClaim.mutateAsync({ claimId: claim.id, reason });
      } else if (isFinanceMode) {
        await financeReject.mutateAsync({ claimId: claim.id, reason });
      } else if (isHrMode) {
        await hrReject.mutateAsync({ claimId: claim.id, reason });
      }
      setShowRejectDialog(false);
      onActionComplete();
    } catch (err) {
      setShowRejectDialog(false);
      if (err instanceof ApiError) setActionError(err.message);
    }
  };

  const handleVerify = async (expense: ExpenseItem) => {
    setActionError(null);
    try {
      await verifyEntry.mutateAsync({
        entryId: expense.id,
        entryType: expense.type,
        action: 'verify',
      });
    } catch (err) {
      if (err instanceof ApiError) setActionError(err.message);
    }
  };

  const handleUnverify = async (expense: ExpenseItem) => {
    setActionError(null);
    try {
      await verifyEntry.mutateAsync({
        entryId: expense.id,
        entryType: expense.type,
        action: 'unverify',
      });
    } catch (err) {
      if (err instanceof ApiError) setActionError(err.message);
    }
  };

  const handleClearFlag = async (expense: ExpenseItem) => {
    setActionError(null);
    try {
      await verifyEntry.mutateAsync({
        entryId: expense.id,
        entryType: expense.type,
        action: 'clear-flag',
      });
    } catch (err) {
      if (err instanceof ApiError) setActionError(err.message);
    }
  };

  const handleConfirmFlag = async () => {
    if (!flagTarget) return;
    if (!flagReasonInput.trim()) {
      setFlagError('Please provide a reason for flagging this entry.');
      return;
    }
    setFlagError(null);
    try {
      const res = await verifyEntry.mutateAsync({
        entryId: flagTarget.entryId,
        entryType: flagTarget.entryType,
        action: 'flag',
        reason: flagReasonInput.trim(),
      });
      setFlagTarget(null);
      setFlagReasonInput('');
      if (res.claimStatus === 'RETURNED_TO_ADMIN') {
        onActionComplete();
      }
    } catch (err) {
      if (err instanceof ApiError) setFlagError(err.message);
    }
  };

  const handleToggleHardcopy = async () => {
    setActionError(null);
    try {
      const res = await markHardcopy.mutateAsync({ received: !hardcopyReceived });
      if (res?.claim) {
        setHardcopyReceived(!!res.claim.hardcopyReceived);
      }
    } catch (err) {
      if (err instanceof ApiError) setActionError(err.message);
    }
  };

  return (
    <>
      {/* Backdrop */}
      <div
        className="fixed inset-0 z-40 bg-black/40 backdrop-blur-sm"
        onClick={onClose}
      />

      {/* Panel — slides in from right */}
      <div className="fixed inset-y-0 right-0 z-40 flex w-full max-w-xl flex-col bg-surface border-l border-border shadow-2xl overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-border shrink-0">
          <div>
            <h2 className="text-base font-semibold text-text">
              {claim.user.name}
            </h2>
            <p className="text-xs text-text-2 mt-0.5">
              {formatMonth(claim.claimMonth)}
              {claim.referenceNumber && (
                <span className="ml-2 font-mono text-primary">{claim.referenceNumber}</span>
              )}
            </p>
          </div>
          <button
            id="review-panel-close-btn"
            onClick={onClose}
            className="p-2 rounded-lg text-text-2 hover:text-text hover:bg-surface-2 transition-colors"
            aria-label="Close panel"
          >
            ✕
          </button>
        </div>

        {/* Scrollable content */}
        <div className="flex-1 overflow-y-auto p-5 space-y-5">
          {claim.status === 'RETURNED_TO_ADMIN' && (
            <div className="rounded-xl border border-warn/40 bg-warn-soft p-4 space-y-2">
              <p className="flex items-center gap-1.5 text-sm font-semibold text-warn">
                <RotateCcw className="h-4 w-4 shrink-0" /> Returned to Admin
              </p>
              {expenses.some((e) => e.flagReason) ? (
                <ul className="space-y-1.5 text-xs">
                  {expenses.filter((e) => e.flagReason).map((e) => (
                    <li key={e.id} className="flex items-start gap-1.5">
                      <Flag className="h-3.5 w-3.5 shrink-0 mt-0.5 text-warn" />
                      <span className="text-text">
                        <span className="font-semibold">{describeFlagger(e.flaggedBy)}:</span>{' '}
                        <span className="italic">"{e.flagReason}"</span>
                      </span>
                    </li>
                  ))}
                </ul>
              ) : (
                <p className="text-xs text-text-2">
                  {expensesLoading ? 'Loading flag details…' : 'All flags have been cleared. Re-verify entries and approve.'}
                </p>
              )}
            </div>
          )}

          {/* Claimant details */}
          <div className="rounded-xl bg-surface-2/40 border border-border p-4 space-y-2">
            <h3 className="text-xs font-semibold text-text-2 uppercase tracking-wide">Claimant</h3>
            <div className="grid grid-cols-2 gap-2 text-sm">
              <div>
                <span className="text-text-2">Name</span>
                <p className="font-medium text-text mt-0.5">{claim.user.name}</p>
              </div>
              <div>
                <span className="text-text-2">Department</span>
                <p className="font-medium text-text mt-0.5">{claim.user.department ?? '—'}</p>
              </div>
              <div>
                <span className="text-text-2">Email</span>
                <p className="font-medium text-text mt-0.5 truncate">{claim.user.email}</p>
              </div>
              <div>
                <span className="text-text-2">Submitted</span>
                <p className="font-medium text-text mt-0.5">{formatDate(claim.updatedAt)}</p>
              </div>
            </div>
          </div>

          {/* Claim summary */}
          <div className="grid grid-cols-3 gap-3">
            <div className="rounded-xl border border-border bg-surface p-3.5 text-center">
              <p className="text-xs text-text-2 font-medium">Total Amount</p>
              <p className="text-base font-bold font-mono text-text mt-1">
                RM {formatCurrency(Number(claim.totalAmount))}
              </p>
            </div>
            <div className="rounded-xl border border-border bg-surface p-3.5 text-center">
              <p className="text-xs text-text-2 font-medium">Mileage</p>
              <p className="text-base font-bold font-mono text-text mt-1">
                RM {formatCurrency(claim.expenseSummary?.mileageTotal ?? 0)}
              </p>
              <p className="text-[10px] text-text-2 mt-0.5">
                {plural(claim.expenseSummary?.mileageCount ?? 0, 'trip')}
              </p>
            </div>
            <div className="rounded-xl border border-border bg-surface p-3.5 text-center">
              <p className="text-xs text-text-2 font-medium">Petty Cash</p>
              <p className="text-base font-bold font-mono text-text mt-1">
                RM {formatCurrency(claim.expenseSummary?.pettyCashTotal ?? 0)}
              </p>
              <p className="text-[10px] text-text-2 mt-0.5">
                {plural(claim.expenseSummary?.pettyCashCount ?? 0, 'receipt')}
              </p>
            </div>
          </div>

          {/* Verification Progress Bar */}
          <div className="rounded-xl border border-border bg-surface p-4 space-y-2">
            <div className="flex items-center justify-between text-xs font-semibold">
              <span className="flex items-center gap-1.5 text-text">
                <ShieldCheck className="h-4 w-4 text-primary" />
                {mode.toUpperCase()} Verification Progress
              </span>
              <span className={cn('font-mono', allVerified ? 'text-ok font-bold' : 'text-text-2')}>
                {verifiedCount} / {totalExpenses} verified
              </span>
            </div>
            <div className="w-full h-2 rounded-full bg-surface-2 overflow-hidden">
              <div
                className={cn('h-full transition-all duration-300', allVerified ? 'bg-ok' : 'bg-primary')}
                style={{ width: `${totalExpenses > 0 ? (verifiedCount / totalExpenses) * 100 : 0}%` }}
              />
            </div>
          </div>

          {/* Hardcopy receipts tracker */}
          <div className="flex items-center justify-between rounded-xl border border-border bg-surface p-3.5">
            <div className="flex items-center gap-2.5">
              <FileCheck className="h-5 w-5 text-primary" />
              <div>
                <p className="text-xs font-semibold text-text">Physical Hardcopy Receipts</p>
                <p className="text-[11px] text-text-2">
                  {hardcopyReceived ? 'Original paper receipts marked received' : 'Original receipts pending arrival'}
                </p>
              </div>
            </div>
            <button
              type="button"
              onClick={handleToggleHardcopy}
              disabled={markHardcopy.isPending}
              className={cn(
                'px-2.5 py-1.5 rounded-lg text-xs font-semibold transition-all flex items-center gap-1.5 min-w-[95px] justify-center',
                hardcopyReceived
                  ? 'bg-ok-soft text-ok border border-ok/20 hover:bg-ok-soft'
                  : 'bg-surface-2 text-text-2 hover:text-text border border-border hover:bg-surface-2'
              )}
            >
              {markHardcopy.isPending ? (
                <span className="h-3.5 w-3.5 rounded-full border-2 border-current border-t-transparent animate-spin" />
              ) : (
                <>
                  {hardcopyReceived ? <Check className="h-3.5 w-3.5" /> : null}
                  <span>{hardcopyReceived ? 'Received' : 'Mark Received'}</span>
                </>
              )}
            </button>
          </div>

          {/* Expenses list */}
          <div>
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-xs font-semibold text-text-2 uppercase tracking-wide">Expense Line Items</h3>
              <span className="text-[11px] text-text-2">Click item to view details</span>
            </div>

            {expensesLoading ? (
              <div className="space-y-2">
                {[1, 2, 3].map((i) => (
                  <div key={i} className="animate-pulse h-16 rounded-xl bg-surface-2" />
                ))}
              </div>
            ) : expenses.length > 0 ? (
              <div className="space-y-3">
                {expenses.map((expense) => {
                  const isMileage = expense.type === 'mileage';
                  const isVerifiedThisRole = isEntryVerifiedForRole(expense);
                  const isFlagged = !!expense.flagReason;

                  return (
                    <div
                      key={expense.id}
                      data-testid="review-entry"
                      className={cn(
                        'rounded-xl border bg-surface/50 transition-colors p-3.5 space-y-2.5',
                        isFlagged
                          ? 'border-warn/50 bg-warn-soft'
                          : isVerifiedThisRole
                          ? 'border-ok/30 bg-ok-soft'
                          : 'border-border hover:border-primary/40'
                      )}
                    >
                      {/* Top row: Clickable item detail summary */}
                      <button
                        type="button"
                        onClick={() => setSelectedExpense(expense)}
                        className="flex w-full flex-wrap items-start text-left focus-visible:outline-none sm:flex-nowrap"
                      >
                        <span className="mr-3 mt-0.5 shrink-0 text-text-2">
                          {isMileage ? <Car className="h-5 w-5 text-info" /> : <Receipt className="h-5 w-5 text-info" />}
                        </span>
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-semibold text-text line-clamp-2 break-words">
                            {isMileage
                              ? ((expense as { purpose?: string }).purpose || `${(expense as { origin: string }).origin} → ${(expense as { destination: string }).destination}`)
                              : (expense as { description: string }).description}
                          </p>
                          {isMileage && (
                            <p className="text-xs text-text-2 break-words leading-relaxed mt-0.5">
                              <span className="text-text/90">{(expense as { origin: string }).origin}</span>
                              <span className="mx-1.5 font-bold text-primary">→</span>
                              <span className="text-text/90">{(expense as { destination: string }).destination}</span>
                              {(expense as { isRoundTrip: boolean }).isRoundTrip && (
                                <>
                                  <span className="mx-1.5 font-bold text-primary">→</span>
                                  <span className="text-text/90">
                                    {(expense as { finalDestination?: string | null; origin: string }).finalDestination
                                      ?? (expense as { origin: string }).origin}
                                  </span>
                                </>
                              )}
                            </p>
                          )}
                          <p className="text-xs text-text-2 mt-0.5 flex items-center gap-1.5 flex-wrap">
                            <span>{formatDate(expense.date)}</span>
                            {!isMileage && (expense as { category?: { name: string } }).category && (
                              <span>· {(expense as { category: { name: string } }).category.name}</span>
                            )}
                            {isMileage && (expense as { isRoundTrip: boolean }).isRoundTrip && <span>· Round trip</span>}
                            {isMileage && (expense as { distanceOverride?: number | null }).distanceOverride != null && (
                              <span className="inline-flex items-center gap-1 text-warn dark:text-amber-400 font-medium">
                                <AlertTriangle className="h-3 w-3" /> Manual distance
                              </span>
                            )}
                          </p>
                        </div>
                        <div className="mt-1.5 flex w-full items-baseline justify-between pl-8 sm:ml-3 sm:mt-0 sm:block sm:w-auto sm:shrink-0 sm:pl-0 sm:text-right">
                          <p className="text-sm font-semibold text-text">
                            RM {formatCurrency(Number(expense.amount))}
                          </p>
                          {isMileage && (
                            <p className="text-xs text-text-2">
                              {Number((expense as { claimableDistance: number }).claimableDistance).toFixed(2)} km
                            </p>
                          )}
                        </div>
                      </button>

                      {/* Flag Warning Banner (if flagged) */}
                      {isFlagged && (
                        <div className="flex items-start justify-between gap-2 rounded-lg bg-warn/10 border border-warn/30 px-3 py-2 text-xs text-warn">
                          <div className="flex items-start gap-1.5 min-w-0">
                            <Flag className="h-3.5 w-3.5 shrink-0 mt-0.5" />
                            <div className="min-w-0">
                              <p className="font-semibold">
                                Flagged by {describeFlagger(expense.flaggedBy)}
                                {expense.flaggedAt && (
                                  <span className="font-normal opacity-80"> · {formatDate(expense.flaggedAt)}</span>
                                )}
                              </p>
                              <p className="italic text-text break-words mt-0.5">"{expense.flagReason}"</p>
                            </div>
                          </div>
                          <button
                            type="button"
                            onClick={() => handleClearFlag(expense)}
                            disabled={verifyEntry.isPending}
                            className="px-2 py-1 rounded bg-warn/20 hover:bg-warn/30 text-warn text-[10px] font-bold uppercase shrink-0 transition-colors"
                          >
                            Clear Flag
                          </button>
                        </div>
                      )}

                      {/* Verification Status Badges & Action Buttons */}
                      <div className="flex items-center justify-between pt-2 border-t border-border/50 text-xs">
                        <div className="flex items-center gap-1.5 flex-wrap">
                          {expense.adminVerified && (
                            <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-semibold bg-info-soft text-info border border-info/20">
                              <Check className="h-2.5 w-2.5" /> Admin
                            </span>
                          )}
                          {expense.financeVerified && (
                            <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-semibold bg-info-soft text-info border border-info/20">
                              <Check className="h-2.5 w-2.5" /> Finance
                            </span>
                          )}
                          {expense.hrVerified && (
                            <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-semibold bg-ok-soft text-ok border border-ok/20">
                              <Check className="h-2.5 w-2.5" /> HR
                            </span>
                          )}
                        </div>

                        {/* Inline Actions */}
                        <div className="flex items-center gap-1.5">
                          {isVerifiedThisRole ? (
                            <button
                              type="button"
                              onClick={() => handleUnverify(expense)}
                              disabled={verifyEntry.isPending}
                              className="inline-flex items-center gap-1 px-2 py-1 rounded-md text-[11px] font-medium bg-ok-soft text-ok hover:bg-danger-soft hover:text-danger transition-colors group/unv"
                              title="Click to unverify"
                            >
                              <Check className="h-3 w-3 group-hover/unv:hidden" />
                              <RotateCcw className="h-3 w-3 hidden group-hover/unv:inline" />
                              <span className="group-hover/unv:hidden">Verified</span>
                              <span className="hidden group-hover/unv:inline">Unverify</span>
                            </button>
                          ) : (
                            <button
                              type="button"
                              onClick={() => handleVerify(expense)}
                              disabled={verifyEntry.isPending || isFlagged}
                              className="inline-flex items-center gap-1 px-2.5 py-1 rounded-md text-[11px] font-semibold bg-primary text-white hover:bg-primary/90 disabled:opacity-50 transition-colors"
                            >
                              <Check className="h-3 w-3" /> Verify
                            </button>
                          )}

                          {!isFlagged && (
                            <button
                              type="button"
                              onClick={() =>
                                setFlagTarget({
                                  entryId: expense.id,
                                  entryType: expense.type,
                                  title: isMileage
                                    ? `${(expense as { origin: string }).origin} → ${(expense as { destination: string }).destination}`
                                    : (expense as { description: string }).description,
                                })
                              }
                              disabled={verifyEntry.isPending}
                              className="p-1 rounded-md text-text-2 hover:text-danger hover:bg-danger-soft transition-colors"
                              title="Flag this entry with a question or issue"
                            >
                              <Flag className="h-3.5 w-3.5" />
                            </button>
                          )}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            ) : (
              <div className="rounded-xl border border-dashed border-border bg-surface/50 p-8 text-center">
                <p className="text-sm text-text-2">No expenses on this claim</p>
              </div>
            )}
          </div>

          {/* View full claim link */}
          <button
            onClick={() => navigate(`/claims/${claim.id}`)}
            className="w-full text-sm text-primary hover:text-primary/80 transition-colors text-center py-1"
          >
            View full claim page →
          </button>

        </div>

        {/* Action bar — sticky bottom */}
        <div className="shrink-0 px-5 py-4 border-t border-border bg-surface flex flex-col gap-2">
          {actionError && (
            <div role="alert" className="rounded-lg bg-danger-soft border border-danger/20 px-4 py-3 text-sm text-danger">
              {actionError}
            </div>
          )}

          {!canApprove && (
            <div className="flex items-center gap-1.5 text-xs text-text font-medium">
              <AlertCircle className="h-3.5 w-3.5 text-warn dark:text-amber-400 shrink-0" />
              <span>
                {!allVerified
                  ? `All ${plural(totalExpenses, 'entry', 'entries')} must be verified before ${canFinalize ? 'approval' : 'the Finance Lead can approve'}.`
                  : anyFlagged
                  ? 'All flagged entries must be resolved.'
                  : 'Physical hardcopy receipts must be marked received.'}
              </span>
            </div>
          )}

          {!canFinalize && (
            <div className="flex items-center gap-1.5 text-xs text-text font-medium">
              <Info className="h-3.5 w-3.5 text-info shrink-0" />
              <span>Final approval or rejection is done by the Finance Lead.</span>
            </div>
          )}

          {canFinalize && (
            <div className="flex gap-3">
              {/* Reject */}
              <button
                id="review-reject-btn"
                onClick={() => setShowRejectDialog(true)}
                disabled={isLoading}
                className="flex-1 flex items-center justify-center gap-2 px-4 py-2.5 rounded-xl border border-danger/40 text-danger text-sm font-medium hover:bg-danger-soft disabled:opacity-50 transition-colors"
              >
                <XCircle className="h-4 w-4" /> Reject
              </button>

              {/* Approve */}
              {showApproveConfirm ? (
                <div className="flex-1 flex gap-2">
                  <button
                    id="review-approve-cancel-btn"
                    onClick={() => setShowApproveConfirm(false)}
                    className="flex-1 px-3 py-2.5 rounded-xl border border-border text-sm font-medium text-text hover:bg-surface-2 transition-colors"
                  >
                    Cancel
                  </button>
                  <button
                    id="review-approve-confirm-btn"
                    onClick={handleApprove}
                    disabled={isLoading || !canApprove}
                    className="flex-1 px-3 py-2.5 rounded-xl bg-primary text-white text-sm font-semibold hover:bg-primary/90 disabled:opacity-50 transition-colors flex items-center justify-center gap-1.5"
                  >
                    {isLoading ? (
                      <span className="w-3.5 h-3.5 rounded-full border-2 border-primary-foreground border-t-transparent animate-spin" />
                    ) : (
                      'Confirm'
                    )}
                  </button>
                </div>
              ) : (
                <button
                  id="review-approve-btn"
                  onClick={() => setShowApproveConfirm(true)}
                  disabled={isLoading || !canApprove}
                  className="flex-[2] flex items-center justify-center gap-2 px-4 py-2.5 rounded-xl bg-primary text-white text-sm font-semibold hover:bg-primary/90 disabled:opacity-50 transition-all hover:scale-105 active:scale-95 shadow-sm"
                >
                  {isAdminMode ? (
                    <ShieldCheck className="h-4 w-4" />
                  ) : isFinanceMode ? (
                    <Briefcase className="h-4 w-4" />
                  ) : (
                    <Award className="h-4 w-4" />
                  )}
                  {approveLabel}
                </button>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Expense detail modal */}
      {selectedExpense?.type === 'mileage' && (
        <MileageFormModal
          claimId={claim.id}
          entryId={selectedExpense.id}
          readOnly
          onClose={() => setSelectedExpense(null)}
        />
      )}

      {selectedExpense?.type === 'petty-cash' && (
        <PettyCashFormModal
          claimId={claim.id}
          entryId={selectedExpense.id}
          readOnly
          onClose={() => setSelectedExpense(null)}
        />
      )}

      {/* Rejection dialog */}
      {showRejectDialog && (
        <div className="z-50 relative">
          <RejectionDialog
            title={isAdminMode ? 'Reject Claim' : isFinanceMode ? 'Finance Rejection' : 'HR Rejection'}
            isLoading={isLoading}
            onConfirm={handleReject}
            onCancel={() => setShowRejectDialog(false)}
          />
        </div>
      )}

      {/* Flag reason dialog */}
      {flagTarget && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-xs" role="dialog" aria-modal="true">
          <div className="w-full max-w-md rounded-2xl bg-surface border border-border p-6 shadow-2xl space-y-4">
            <div className="flex items-start gap-3">
              <div className="w-9 h-9 rounded-full bg-danger-soft text-danger flex items-center justify-center shrink-0">
                <Flag className="h-5 w-5" />
              </div>
              <div className="min-w-0 flex-1">
                <h3 className="text-base font-semibold text-text">Flag Entry for Correction</h3>
                <p className="text-xs text-text-2 truncate mt-0.5">{flagTarget.title}</p>
              </div>
            </div>

            <div className="rounded-lg bg-warn-soft0/10 border border-warn/20 p-3 text-xs space-y-1">
              <p className="font-semibold text-amber-800 dark:text-amber-400 flex items-center gap-1.5">
                <AlertTriangle className="h-3.5 w-3.5 text-warn dark:text-amber-400 shrink-0" /> {flagReturnsClaim ? 'Return to Admin' : 'Flag for Correction'}
              </p>
              <p className="text-text text-[11px] leading-relaxed">
                {flagReturnsClaim
                  ? 'Flagging this expense will return the entire claim to Admin review and notify the claimant.'
                  : 'Flagging this expense marks it for correction and clears its verification. The claim stays in its current review stage and the claimant is not notified.'}
              </p>
            </div>

            <div className="space-y-1.5">
              <label htmlFor="flag-reason-input" className="text-xs font-medium text-text">
                Reason for Flagging <span className="text-danger">*</span>
              </label>
              <textarea
                id="flag-reason-input"
                rows={3}
                value={flagReasonInput}
                onChange={(e) => setFlagReasonInput(e.target.value)}
                placeholder="e.g. Missing official tax receipt, mileage distance does not match route..."
                className="w-full rounded-xl border border-input bg-surface p-3 text-xs text-text placeholder:text-text-2 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
              />
              {flagError && (
                <p className="text-xs text-danger">{flagError}</p>
              )}
            </div>

            <div className="flex gap-2.5 pt-2">
              <button
                type="button"
                onClick={() => {
                  setFlagTarget(null);
                  setFlagReasonInput('');
                  setFlagError(null);
                }}
                className="flex-1 px-4 py-2.5 rounded-xl border border-border text-xs font-medium text-text hover:bg-surface-2 transition-colors"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleConfirmFlag}
                disabled={verifyEntry.isPending}
                className="flex-1 px-4 py-2.5 rounded-xl bg-danger text-white text-xs font-semibold hover:bg-danger/90 disabled:opacity-50 transition-colors flex items-center justify-center gap-1.5"
              >
                {verifyEntry.isPending ? 'Flagging…' : flagReturnsClaim ? 'Flag & Return Claim' : 'Flag Entry'}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
